import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactElement,
} from "react";
import {
  Check,
  ChevronDown,
  Globe2,
  LayoutGrid,
  Search,
  Smartphone,
} from "lucide-react";

import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

import type { WorkspaceListItem } from "./api";
import { workspaceInitial, workspaceStatus } from "./presentation";

export function WorkspaceSwitcher({
  current,
  workspaces,
}: {
  current: WorkspaceListItem;
  workspaces: WorkspaceListItem[];
}): ReactElement {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const container = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const searchInput = useRef<HTMLInputElement>(null);
  const orderedWorkspaces = useMemo(
    () => {
      const uniqueWorkspaces = new Map(
        workspaces.map((workspace) => [workspace.id, workspace]),
      );
      uniqueWorkspaces.set(current.id, current);
      return [...uniqueWorkspaces.values()].sort((left, right) => {
        if (left.id === current.id) return -1;
        if (right.id === current.id) return 1;
        return left.name.localeCompare(right.name);
      });
    },
    [current, workspaces],
  );
  const filteredWorkspaces = useMemo(() => {
    const needle = query.trim().toLocaleLowerCase();
    if (!needle) return orderedWorkspaces;
    return orderedWorkspaces.filter((workspace) =>
      [workspace.name, workspace.id].some((value) =>
        value.toLocaleLowerCase().includes(needle),
      ),
    );
  }, [orderedWorkspaces, query]);

  useEffect(() => {
    if (!open) return;

    searchInput.current?.focus();

    function closeOnOutsideClick(event: PointerEvent): void {
      if (!container.current?.contains(event.target as Node)) {
        setOpen(false);
        setQuery("");
      }
    }

    function closeOnEscape(event: KeyboardEvent): void {
      if (event.key !== "Escape") return;
      setOpen(false);
      setQuery("");
      trigger.current?.focus();
    }

    document.addEventListener("pointerdown", closeOnOutsideClick);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("pointerdown", closeOnOutsideClick);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [open]);

  return (
    <div ref={container} className="relative min-w-0 flex-1">
      <button
        ref={trigger}
        type="button"
        className="flex w-full max-w-full min-w-0 items-center gap-3 rounded-xl p-1 pr-2 text-left transition-colors hover:bg-[#f3f3ef] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#9cafaa] sm:w-auto sm:max-w-[22rem]"
        aria-label={`Switch workspace. Current workspace: ${current.name}`}
        aria-expanded={open}
        aria-controls="workspace-switcher"
        onClick={() => {
          setOpen((value) => !value);
          if (open) setQuery("");
        }}
      >
        <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-[#20272b] text-sm font-semibold text-white">
          {workspaceInitial(current.name)}
        </span>
        <span className="min-w-0">
          <span className="block truncate text-sm font-semibold">
            {current.name}
          </span>
          <span className="flex items-center gap-1.5 text-[11px] text-[#81837b]">
            {current.artifact.kind === "website" ? (
              <Globe2 className="size-3" aria-hidden="true" />
            ) : (
              <Smartphone className="size-3" aria-hidden="true" />
            )}
            {workspaceStatus(current)}
          </span>
        </span>
        <ChevronDown
          className={cn(
            "ml-1 size-4 shrink-0 text-[#85877f] transition-transform",
            open && "rotate-180",
          )}
          aria-hidden="true"
        />
      </button>

      {open ? (
        <div
          id="workspace-switcher"
          className="absolute left-0 top-[calc(100%+0.5rem)] z-50 w-[min(21rem,calc(100vw-1.5rem))] overflow-hidden rounded-2xl border border-[#d9d9d3] bg-white shadow-[0_18px_55px_rgba(31,38,34,0.16)]"
        >
          <div className="border-b border-[#ecece7] px-4 py-3 text-xs font-semibold uppercase tracking-[0.14em] text-[#777a72]">
            Workspaces
          </div>
          {orderedWorkspaces.length > 5 ? (
            <div className="relative border-b border-[#ecece7] p-2">
              <label htmlFor="workspace-switcher-search" className="sr-only">
                Search workspaces
              </label>
              <Search
                className="pointer-events-none absolute left-5 top-1/2 size-3.5 -translate-y-1/2 text-[#8a8c84]"
                aria-hidden="true"
              />
              <Input
                ref={searchInput}
                id="workspace-switcher-search"
                type="search"
                value={query}
                onChange={(event) => setQuery(event.currentTarget.value)}
                placeholder="Find a workspace"
                className="h-9 rounded-lg border-[#deded8] bg-[#fafaf8] pl-8 text-sm shadow-none"
              />
            </div>
          ) : null}
          <div className="max-h-72 overflow-y-auto p-1.5">
            {filteredWorkspaces.map((workspace) => {
              const isCurrent = workspace.id === current.id;
              return (
                <a
                  key={workspace.id}
                  href={workspacePath(workspace.id)}
                  aria-current={isCurrent ? "page" : undefined}
                  className={cn(
                    "flex items-center gap-3 rounded-xl px-2.5 py-2.5 transition-colors hover:bg-[#f3f4f1]",
                    isCurrent && "bg-[#f3f6f4]",
                  )}
                  onClick={(event) => {
                    if (!isCurrent) return;
                    event.preventDefault();
                    setOpen(false);
                    setQuery("");
                  }}
                >
                  <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-[#e5eeeb] text-xs font-semibold text-[#315d54]">
                    {workspaceInitial(workspace.name)}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium">
                      {workspace.name}
                    </span>
                    <span className="block text-[11px] text-[#85877f]">
                      {workspaceStatus(workspace)}
                    </span>
                  </span>
                  {isCurrent ? (
                    <Check className="size-4 shrink-0 text-[#3d6e63]" aria-hidden="true" />
                  ) : null}
                </a>
              );
            })}
            {filteredWorkspaces.length === 0 ? (
              <p className="px-3 py-6 text-center text-xs text-[#85877f]">
                No matching workspaces
              </p>
            ) : null}
          </div>
          <div className="border-t border-[#ecece7] p-1.5">
            <a
              href="/workspaces"
              className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors hover:bg-[#f3f4f1]"
            >
              <LayoutGrid className="size-4 text-[#6e756f]" aria-hidden="true" />
              All workspaces
            </a>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function workspacePath(id: string): string {
  return `/workspaces/${encodeURIComponent(id)}`;
}
